import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { SaveSearchFrequencyEnum } from 'common/data/save-search/frequency.enum';
import { SaveSearchModel } from 'common/data/save-search/model';
import { SaveSearchStore } from 'common/data/save-search/store';
import { functionNoop } from 'common/helper/function/noop';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { FilterParamsEnum } from 'common/module/filter/params.enum';
import { FilterStore } from 'common/module/filter/store';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { SaveSearchTracker } from '../tracker';
import { SaveSearchFormFieldsInterface } from './fields.interface';
import { SaveSearchFormPropsInterface } from './props.interface';

export class SaveSearchFormViewStore extends DataStore<SaveSearchFormPropsInterface> {
  /**
   * Maximum characters limit for field (inclusive)
   */
  public static readonly MAX_CHARACTERS_LIMIT: number = 256;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private saveSearchStoreService: SaveSearchStore,
    private filterStoreService: FilterStore,
    private saveSearchTracker: SaveSearchTracker,
    private userAuthenticationService: UserAuthenticationServiceInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritdoc
   */
  public initialize(options: Partial<SaveSearchFormPropsInterface> = {}): void {
    this.state = {
      title: i18nTranslate('save_search:form:add:title'),
      isLoading: false,
      error: null,
      fields: this.getDefaultFields(),
      userHasEmail: this.userHasEmail(),
      onSubmit: this.onSubmit,
      ...options,
      onSuccess: options.onSuccess || functionNoop,
      sendTealiumEvent: this.sendTealiumEvent,
    };

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.updateUserSucceeded, this.onUpdateUserSucceeded);
  }

  /**
   * @inheritdoc
   */
  public destroy(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .removeListener(UserAuthenticationEvent.updateUserSucceeded, this.onUpdateUserSucceeded);
  }

  private onUpdateUserSucceeded = (): void => {
    this.setState({
      ...this.state,
      userHasEmail: true,
    });
  };

  private sendTealiumEvent = (type: 'open' | 'submit' | 'skip'): void => {
    switch (type) {
      case 'open':
        this.saveSearchTracker.onCompleteProfileModalOpen();
        break;
      case 'submit':
        this.saveSearchTracker.onCompleteProfileModalSubmit(this.userAuthenticationService.getUser()?.email);
        break;
      case 'skip':
        this.saveSearchTracker.onCompleteProfileModalSkip();
        break;
    }
  };

  /**
   * Get the default fields
   */
  private getDefaultFields = (): SaveSearchFormFieldsInterface => {
    return {
      id: null,
      name: '',
      formatted_filters: null,
      frequency: this.userHasEmail() ? SaveSearchFrequencyEnum.DAILY : SaveSearchFrequencyEnum.OFF,
    };
  };

  private userHasEmail = (): boolean => {
    const user = this.userAuthenticationService.getUser();
    return !!user?.email;
  };

  /**
   * On form submit callback
   */
  private onSubmit = (name: string, frequency: SaveSearchFrequencyEnum, id?: string): void => {
    this.setState({
      ...this.state,
      isLoading: true,
      error: null,
    });

    this.createOrUpdate(name, frequency, id)
      .then(() => {
        this.setState({
          ...this.state,
          isLoading: false,
        });
        this.state.onSuccess();
      })
      .catch(() => {
        this.setState({
          ...this.state,
          isLoading: false,
          error: i18nTranslate('Something went wrong'),
        });
      });
  };

  /**
   * Create or update record based on the id
   */
  private createOrUpdate = (
    name: string,
    frequency: SaveSearchFrequencyEnum,
    id?: string
  ): Promise<SaveSearchModel[]> => {
    if (id) {
      return this.saveSearchStoreService.update(id, { name, frequency }).then((response) => {
        this.saveSearchTracker.onEditSuccess(id);
        return response;
      });
    } else {
      return this.saveSearchStoreService
        .create(name, frequency, this.filterStoreService.getState())
        .then((response) => {
          this.saveSearchTracker.onCreateSuccess(
            this.filterStoreService.getState()?.[FilterParamsEnum.locationsIds]?.value
          );
          return response;
        });
    }
  };
}
